/**
 * store-context.ts
 *
 * Single source of truth for resolving the active store context.
 *
 * Priority order:
 *   1. URL query param `?storeName=...`  (cross-tab navigation hint)
 *   2. In localStorage (persisted user session)
 *
 * Rules enforced here:
 *   - Never write a partial Store object.
 *   - URL param always wins over localStorage (avoids race conditions).
 *   - All callers use these helpers — no one parses window.location directly.
 */

import { CONSTANTS_KEYS } from "@/constants/store";
import { Store } from "@/interfaces/store.interface";

const isBrowser = () => typeof window !== "undefined";

// ---------------------------------------------------------------------------
// URL helpers
// ---------------------------------------------------------------------------

/**
 * Reads `?storeName` from the current or a provided URL.
 * Returns null if not present or if running on the server.
 */
export const getStoreNameFromUrl = (href?: string): string | null => {
  if (!isBrowser() && !href) return null;
  try {
    const url = new URL(href ?? window.location.href);
    return url.searchParams.get("storeName");
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

/**
 * Reads the persisted Store object from localStorage.
 * Returns null on failure or when running server-side.
 */
export const readStoredStore = (): Store | null => {
  if (!isBrowser()) return null;
  try {
    const raw = localStorage.getItem(CONSTANTS_KEYS.SELECTED_STORE);
    return raw ? (JSON.parse(raw) as Store) : null;
  } catch {
    return null;
  }
};

/**
 * Persists a full Store object (or clears it when null).
 * This is the ONLY place that should call localStorage.setItem for SELECTED_STORE.
 */
export const persistSelectedStore = (store: Store | null): void => {
  if (!isBrowser()) return;
  if (!store) {
    localStorage.removeItem(CONSTANTS_KEYS.SELECTED_STORE);
    return;
  }
  localStorage.setItem(CONSTANTS_KEYS.SELECTED_STORE, JSON.stringify(store));
};

// ---------------------------------------------------------------------------
// Resolution helpers
// ---------------------------------------------------------------------------

/**
 * Given the full list of stores available to the current user, resolves
 * which Store should be considered "selected", following priority:
 *   1. URL query param `?storeName`
 *   2. Previously persisted store (matched by id or storeName for safety)
 *
 * Returns null when no valid store can be resolved.
 */
export const resolveSelectedStore = (stores: Store[]): Store | null => {
  if (!stores.length) return null;

  const urlStoreName = getStoreNameFromUrl();
  if (urlStoreName) {
    return stores.find((s) => s.storeName === urlStoreName) ?? null;
  }

  const stored = readStoredStore();
  if (!stored) return null;

  // Prefer matching by id for stability; fall back to storeName.
  return (
    stores.find((s) => s.id === stored.id || s.storeName === stored.storeName) ??
    stored
  );
};

/**
 * Returns the active store key (storeName string) for use in API request headers.
 *
 * Priority: URL param → localStorage.
 * This is intentionally a synchronous, side-effect-free read.
 */
export const getActiveStoreKey = (): string =>
  getStoreNameFromUrl() ?? readStoredStore()?.storeName ?? "";

// ---------------------------------------------------------------------------
// Navigation helpers
// ---------------------------------------------------------------------------

/**
 * Builds the URL to the Campaign Detail page, embedding the store hint
 * so the target tab can resolve its context without touching localStorage.
 */
export const buildCampaignDetailUrl = (
  campaignId: string,
  storeName?: string,
): string => {
  const url = new URL(`/campaigns/${campaignId}`, window.location.origin);
  if (storeName) url.searchParams.set("storeName", storeName);
  return url.toString();
};
